import { call, put, select, takeLatest } from 'redux-saga/effects'
import { PayloadAction } from '@reduxjs/toolkit'
import { PaymentIntent, PaymentIntentResult } from '@stripe/stripe-js'

import chargeModel from '@/core/models/charge.model'
import { Consultation } from '@/core/models/consultation.model'

import {
  acceptPayment as acceptPaymentAction,
  acceptPaymentSuccess as acceptPaymentSuccessAction,
  acceptPaymentFailure as acceptPaymentFailureAction,
  AcceptPaymentActionPayload,
  openStellair as openStellairAction,
  acceptPaymentFromUserID as acceptPaymentFromUserIDAction,
} from '@/redux/reducers/charge.reducer'
import { selectConsultationById } from '@/redux/reducers/consultation.reducer'

import { handleError } from '@/utils/errors'

export function* acceptPayment(action: PayloadAction<AcceptPaymentActionPayload>) {
  try {
    const { consultationId, stripe, elements } = action.payload
    const consultation: Consultation = yield select((state) => selectConsultationById(state, consultationId))
    const paymentIntent: PaymentIntent = yield call(chargeModel.startPayment, consultationId)
    const { client_secret: clientSecret } = paymentIntent

    if (!clientSecret) {
      yield put(acceptPaymentFailureAction(handleError(new Error('client secret is null'))))
      return
    }

    const { error }: PaymentIntentResult = yield stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: elements.getElement('cardNumber')!,
        billing_details: {
          name: `${consultation.patient.userInfo.name} ${consultation.patient.userInfo.surname}`,
        },
      },
    })

    if (error) {
      yield put(acceptPaymentFailureAction(handleError(error)))
      return
    }

    yield call(chargeModel.confirmPayment, consultationId)
    yield put(acceptPaymentSuccessAction())
  } catch (exception) {
    yield put(acceptPaymentFailureAction(handleError(exception)))
  }
}

export function* acceptPaymentFromUserID(action: PayloadAction<AcceptPaymentActionPayload & { receiverID: string }>) {
  try {
    const { consultationId, stripe, elements, receiverID } = action.payload
    const consultation: Consultation = yield select((state) => selectConsultationById(state, consultationId))
    const paymentIntent: PaymentIntent = yield call(chargeModel.startPaymentByReceiverID, consultationId, receiverID)
    const { client_secret: clientSecret } = paymentIntent

    if (!clientSecret) {
      yield put(acceptPaymentFailureAction(handleError(new Error('client secret is null'))))
      return
    }

    const { error }: PaymentIntentResult = yield stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: elements.getElement('cardNumber')!,
        billing_details: {
          name: `${consultation.patient.userInfo.name} ${consultation.patient.userInfo.surname}`,
        },
      },
    })

    if (error) {
      yield put(acceptPaymentFailureAction(handleError(error)))
      return
    }

    yield call(chargeModel.confirmPayment, consultationId)
    yield put(acceptPaymentSuccessAction())
  } catch (exception) {
    yield put(acceptPaymentFailureAction(handleError(exception)))
  }
}

export function* openStellair(action: PayloadAction<{ consultationId: string }>) {
  try {
    const url: string = yield call(chargeModel.openStellair, action.payload.consultationId)
    window.open(url, '_blank')
  } catch (exception) {
    yield put(acceptPaymentFailureAction(handleError(exception)))
  }
}



const watchChargeSagas = [
  takeLatest(acceptPaymentAction.type, acceptPayment),
  takeLatest(openStellairAction.type, openStellair),
  takeLatest(acceptPaymentFromUserIDAction.type, acceptPaymentFromUserID),
]

export default watchChargeSagas
